import React, { useState, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  SimpleGrid,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Progress,
  Badge,
  Button,
  Select,
  Input,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Flex,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiTarget,
  FiBarChart,
  FiCalendar,
  FiFilter,
  FiRefreshCw,
  FiDownload,
  FiEye,
  FiPlus,
} from "react-icons/fi";
import {
  useGetOrganizationProductivity,
  useGetProductivityRanking,
  useCalculateProductivityMetrics,
} from "../../services/productivityService";
import { useGetEmployees } from "../../services/employeeService";
import { useFetchDepartments } from "../../services/configurationService";
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import BranchAccessRestriction from "../../components/BranchAccessRestriction";
import BranchFilter from "../../components/BranchFilter";

const ProductivityReports = ({ selectedBranch: propSelectedBranch }) => {
  const { canAccessFeature } = usePlanRestriction();
  
  const [selectedPeriod, setSelectedPeriod] = useState("monthly");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedBranch, setSelectedBranch] = useState(propSelectedBranch || "all");
  const [dateRange, setDateRange] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const toast = useToast();

    // Fetch data with branch filtering
  const { 
    organizationProductivityData, 
    organizationProductivityIsLoading, 
    organizationProductivityError 
  } = useGetOrganizationProductivity({
    filters: { ...(selectedBranch !== 'all' && { branch_id: selectedBranch }) }
  });
  const { 
    productivityRankingData, 
    productivityRankingIsLoading, 
    productivityRankingError 
  } = useGetProductivityRanking({
    filters: {
      period: selectedPeriod,
      limit: 10,
      ...(selectedBranch !== 'all' && { branch_id: selectedBranch })
    }
  });
  const { getEmployeesData } = useGetEmployees({
    enabled: true,
    filters: { ...(selectedBranch !== 'all' && { branch_id: selectedBranch }) }
  });
  const { getDepartmentData } = useFetchDepartments({ enabled: true });

  // Calculate metrics hook
  const { calculateMetricsPayload, calculateMetricsIsLoading } = useCalculateProductivityMetrics(() => {
    toast({
      title: "Metrics calculated",
      description: "Productivity metrics have been calculated and saved successfully",
      status: "success",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  });

  const handleCalculateMetrics = async (employeeId) => {
    try {
      await calculateMetricsPayload({
        employee_id: employeeId,
        date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error("Error calculating metrics:", error);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === "improving") return <FiTrendingUp color="green" />;
    if (trend === "declining") return <FiTrendingDown color="red" />;
    return <FiBarChart color="gray" />;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "green";
    if (score >= 60) return "yellow";
    if (score >= 40) return "orange";
    return "red";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Excellent";
    if (score >= 60) return "Good";
    if (score >= 40) return "Average";
    return "Poor";
  };

  // Feature access is now only restricted by user role (branch vs main branch)
  // All Main Branch users have access to Productivity Reports regardless of plan



  return (
    <Box w="100%" bg="gray.50" minH="100vh" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Productivity Reports
            </Heading>
            <Text color="gray.600" fontSize="md">
              Monitor and analyze employee productivity across the organization
            </Text>
          </VStack>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiRefreshCw />}
              colorScheme="blue"
              variant="outline"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
            <Button
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
            >
              Export Report
            </Button>
          </HStack>
        </HStack>

                 {/* Filters */}
         <Card>
           <CardBody>
             <HStack spacing={4} wrap="wrap">
               <Select
                 value={selectedPeriod}
                 onChange={(e) => setSelectedPeriod(e.target.value)}
                 maxW="150px"
               >
                 <option value="daily">Daily</option>
                 <option value="weekly">Weekly</option>
                 <option value="monthly">Monthly</option>
                 <option value="quarterly">Quarterly</option>
               </Select>
               
                               {/* Branch filter is now handled at the parent level */}
               
               <Select
                 value={selectedDepartment}
                 onChange={(e) => setSelectedDepartment(e.target.value)}
                 maxW="200px"
               >
                 <option value="all">All Departments</option>
                 {getDepartmentData?.map((dept) => (
                   <option key={dept.id} value={dept.id}>
                     {dept.name}
                   </option>
                 ))}
               </Select>
               
               <Input
                 type="date"
                 value={dateRange.start_date}
                 onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                 maxW="150px"
               />
               
               <Input
                 type="date"
                 value={dateRange.end_date}
                 onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                 maxW="150px"
               />
             </HStack>
           </CardBody>
         </Card>

        {/* Organization Overview */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <StatCard
            title="Overall Productivity"
            value={organizationProductivityData?.overall_productivity || 0}
            suffix="%"
            trend={organizationProductivityData?.trends?.current_month || "stable"}
            color="blue"
            icon={<FiTarget />}
            isLoading={organizationProductivityIsLoading}
          />
          <StatCard
            title="Total Employees"
            value={organizationProductivityData?.total_employees || 0}
            trend="stable"
            color="green"
            icon={<FiUsers />}
            isLoading={organizationProductivityIsLoading}
          />
          <StatCard
            title="Active Employees"
            value={organizationProductivityData?.active_employees || 0}
            trend="stable"
            color="purple"
            icon={<FiUsers />}
            isLoading={organizationProductivityIsLoading}
          />
          <StatCard
            title="Average Attendance"
            value={organizationProductivityData?.overall_attendance || 0}
            suffix="%"
            trend="stable"
            color="orange"
            icon={<FiCalendar />}
            isLoading={organizationProductivityIsLoading}
          />
        </SimpleGrid>

        {/* Key Insights */}
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md" color="gray.900">
                Key Insights
              </Heading>
              {organizationProductivityIsLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} color="gray.600">Loading insights...</Text>
                </Box>
              ) : organizationProductivityError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load insights. Please try again.</Text>
                </Alert>
              ) : organizationProductivityData?.key_insights ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  {organizationProductivityData.key_insights.map((insight, index) => (
                    <Alert key={index} status="info" borderRadius="md">
                      <AlertIcon />
                      <Text fontSize="sm">{insight}</Text>
                    </Alert>
                  ))}
                </SimpleGrid>
              ) : (
                <Text color="gray.500" fontSize="sm">No insights available</Text>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Strategic Recommendations */}
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <Heading size="md" color="gray.900">
                Strategic Recommendations
              </Heading>
              {organizationProductivityIsLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} color="gray.600">Loading recommendations...</Text>
                </Box>
              ) : organizationProductivityError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load recommendations. Please try again.</Text>
                </Alert>
              ) : organizationProductivityData?.strategic_recommendations ? (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                  {organizationProductivityData.strategic_recommendations.map((rec, index) => (
                    <Box
                      key={index}
                      p={4}
                      bg="blue.50"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="blue.200"
                    >
                      <Text fontSize="sm" color="blue.800">
                        {rec}
                      </Text>
                    </Box>
                  ))}
                </SimpleGrid>
              ) : (
                <Text color="gray.500" fontSize="sm">No recommendations available</Text>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Productivity Rankings */}
        <Card>
          <CardBody>
            <VStack align="start" spacing={4}>
              <HStack justify="space-between" w="100%">
                <Heading size="md" color="gray.900">
                  Top Performers
                </Heading>
                <Button
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  size="sm"
                  onClick={onOpen}
                >
                  Calculate Metrics
                </Button>
              </HStack>
              
              {productivityRankingIsLoading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="md" color="blue.500" />
                  <Text mt={2} color="gray.600">Loading rankings...</Text>
                </Box>
              ) : productivityRankingError ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Failed to load productivity rankings. Please try again.</Text>
                </Alert>
              ) : (
                <Box w="100%" overflowX="auto">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Rank</Th>
                        <Th>Employee</Th>
                        <Th>Productivity Score</Th>
                        <Th>Attendance Rate</Th>
                        <Th>Efficiency Rate</Th>
                        <Th>Work Hours</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {productivityRankingData?.top_performers?.length > 0 ? (
                        productivityRankingData.top_performers.map((employee, index) => (
                          <Tr key={employee.employee_id}>
                            <Td>
                              <Badge
                                colorScheme={index < 3 ? "gold" : "gray"}
                                variant="solid"
                                borderRadius="full"
                                px={3}
                              >
                                #{employee.rank}
                              </Badge>
                            </Td>
                            <Td fontWeight="500">{employee.employee_name}</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Text fontWeight="600">
                                  {employee.productivity_score.toFixed(1)}%
                                </Text>
                                <Badge
                                  colorScheme={getScoreColor(employee.productivity_score)}
                                  variant="subtle"
                                >
                                  {getScoreLabel(employee.productivity_score)}
                                </Badge>
                              </HStack>
                            </Td>
                            <Td>{employee.attendance_rate.toFixed(1)}%</Td>
                            <Td>{employee.efficiency_rate.toFixed(1)}%</Td>
                            <Td>{employee.work_hours.toFixed(1)}h</Td>
                            <Td>
                              <HStack spacing={2}>
                                <Tooltip label="View Details">
                                  <IconButton
                                    icon={<FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setSelectedEmployee(employee)}
                                  />
                                </Tooltip>
                                <Tooltip label="Calculate Metrics">
                                  <IconButton
                                    icon={<FiRefreshCw />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleCalculateMetrics(employee.employee_id)}
                                    isLoading={calculateMetricsIsLoading}
                                  />
                                </Tooltip>
                              </HStack>
                            </Td>
                          </Tr>
                        ))
                      ) : (
                        <Tr>
                          <Td colSpan={7} textAlign="center" py={8}>
                            <Text color="gray.500" fontSize="sm">No productivity data available</Text>
                          </Td>
                        </Tr>
                      )}
                    </Tbody>
                  </Table>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Calculate Metrics Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Calculate Productivity Metrics</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text color="gray.600">
                Select an employee to calculate their productivity metrics for today.
              </Text>
              <Select
                placeholder="Select Employee"
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                {(getEmployeesData?.employees || []).map((employee) => (
                  <option key={employee.employee_id} value={employee.employee_id}>
                    {employee.first_name} {employee.last_name}
                  </option>
                ))}
              </Select>
              <Button
                colorScheme="blue"
                onClick={() => {
                  if (selectedEmployee) {
                    handleCalculateMetrics(selectedEmployee);
                    onClose();
                  }
                }}
                isLoading={calculateMetricsIsLoading}
                isDisabled={!selectedEmployee}
              >
                Calculate Metrics
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ title, value, suffix = "", trend, color, icon, isLoading = false }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
    orange: { bg: "orange.50", color: "orange.600", border: "orange.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  const getTrendIcon = (trend) => {
    if (trend === "improving") return <FiTrendingUp color="green" />;
    if (trend === "declining") return <FiTrendingDown color="red" />;
    return <FiBarChart color="gray" />;
  };

  return (
    <Card>
      <CardBody>
        <HStack spacing={4} align="center">
          <Box
            p={3}
            borderRadius="12px"
            bg={colors.bg}
            color={colors.color}
            border="1px solid"
            borderColor={colors.border}
          >
            {icon}
          </Box>
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              {title}
            </Text>
            {isLoading ? (
              <Box>
                <Spinner size="sm" color="blue.500" mr={2} />
                <Text fontSize="sm" color="gray.500">Loading...</Text>
              </Box>
            ) : (
              <>
                <Text fontSize="2xl" color="gray.900" fontWeight="700">
                  {value}{suffix}
                </Text>
                <HStack spacing={1}>
                  {getTrendIcon(trend)}
                  <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                    {trend}
                  </Text>
                </HStack>
              </>
            )}
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default ProductivityReports; 